import ServiceCostTable from '../../components/ReportTable/ServiceReportTable';
import { useSelector } from 'react-redux';
import { accountState } from '../../store';
import React, { useEffect, useRef, useState } from 'react';
import { TimeFrameAPI } from '../../components/ServicesReports/Header';
import { costFormatter2 } from '../../utils/config';
import { ALL_VALUE } from '../Invoice/config';
import { CLOUD_PROVIDER_SERVICES, currencies } from '../../utils/constants';
import { exportToFile } from '../../utils/formateXLSX';
import CostAnalysisHeader from './Header';
import FilterDrawer from './FilterDrawer';
import DateRangePicker from './DateRangePicker';
import { format, subDays } from 'date-fns';
import './costAnalysis.css';
// import { FILTER_CATEGORIES } from './FilterData';
import { useFetchData } from '../../hooks/useFetchData';
import { config, PARENT_FILTERS } from './config';
import * as echarts from 'echarts';
import { echartsColorPalette } from '../HealthMonitoring/config';
import Chart from '../../components/MasterComponent/EchartsSetup/Echarts';
import CustomLegend from '../../components/MasterComponent/EchartsSetup/CustomLegend';
import TitleHeader from '../../components/MasterComponent/TitleHeader/TitleHeader';
import { SelectedAccount } from '../../components/SelectAccount/SelectAccount';
import GraphLoader from '../../components/ServicesReports/GraphLoader';

const CostAnalysis: React.FC<any> = () => {
  const selectedAccountInfo = useSelector(accountState);

  // Set default date range (today - 6 days)
  const today = new Date();
  const defaultStartDate = subDays(today, 6);
  const defaultEndDate = today;

  // Date range state
  const [startDate, setStartDate] = useState<Date>(defaultStartDate);
  const [endDate, setEndDate] = useState<Date>(defaultEndDate);

  const [selectedGroupBy, setSelectedGroupBy] = useState<TimeFrameAPI>('day');
  // const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(
  //   selectedAccountInfo.selectedSubscriptionId
  // );
  const [selectedAccountId, setSelectedAccountId] = React.useState<any>(
    selectedAccountInfo.selectedAccountId
  );
  const [servicesData, setServicesData] = useState<any>({});

  const [activeView, setActiveView] = useState<'table' | 'chart'>('table');
  const [chartInstance, setChartInstance] = useState<echarts.ECharts | null>(
    null
  );
  const createCategoryKey = (category: {
    id: string;
    label: string;
  }): string => {
    return `${category.id}:${category.label}`;
  };

  // Group By buttons hooks
  const [groupBySelectedKey, setGroupBySelectedKey] = useState(() => {
    const firstCategory = PARENT_FILTERS[0];
    return createCategoryKey(firstCategory);
  });
  const [selectedCategoryLabel, setSelectedCategoryLabel] = useState(
    PARENT_FILTERS[0].label
  );
  const [
    groupBySelectedCategory,
    setGroupBySelectedCategory,
  ] = useState<string>(PARENT_FILTERS[0].id);

  //Filter Drawer hooks///////////

  const [selectedFilterCategoryInfo, setSelectedFilterCategoryInfo] = useState<{
    id: string;
    label: string;
  } | null>(null);

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const [selectedFilterCategory, setSelectedFilterCategory] = useState<
    string | null
  >(null);
  const [selectedFilterOptions, setSelectedFilterOptions] = useState<
    Record<string, string[]>
  >({});

  // UI-specific selections - stores by unique category key (for UI display)
  const [
    uiSelectedOptionsByCategory,
    setUiSelectedOptionsByCategory,
  ] = useState<Record<string, string[]>>({});

  const reqBodyRef = useRef({
    groupByColumn: groupBySelectedCategory,
    timeGranularity: selectedGroupBy,
    startDate: format(startDate, 'yyyy-MM-dd'),
    endDate: format(endDate, 'yyyy-MM-dd'),
    filters: selectedFilterOptions,
    ...(selectedAccountId !== ALL_VALUE
      ? { accountId: selectedAccountId }
      : {}),
  });

  const { fetch, data, loader: isSubmitting } = useFetchData({
    fetchData: () => config.getCostUsageReport(reqBodyRef.current),

    fetchOnMount: true,
  });

  useEffect(() => {
    reqBodyRef.current = {
      groupByColumn: groupBySelectedCategory,
      timeGranularity: selectedGroupBy,
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      filters: selectedFilterOptions,
      ...(selectedAccountId !== ALL_VALUE
        ? { accountId: selectedAccountId }
        : {}),
    };
  }, [
    groupBySelectedCategory,
    selectedGroupBy,
    startDate,
    endDate,
    selectedFilterOptions,
    selectedAccountId,
  ]);
  let isCanceled = false;

  useEffect(() => {
    if (data && !isSubmitting && !isCanceled) {
      setServicesData(data);
    }

    return () => {
      isCanceled = true;
    };
  }, [data, isSubmitting]);
  const [isDownloading, setIsDownloading] = useState(false);
  const {
    fetch: fetchCostReport,
    data: fetchCostReportDownloadStatus,
    loader: isDownloadLoading,
  } = useFetchData({
    fetchData: () => config.downloadCostReport(reqBodyRef.current),
    fetchOnMount: false,
  });

  useEffect(() => {
    if (isDownloading && !isDownloadLoading && fetchCostReportDownloadStatus) {
      if (
        fetchCostReportDownloadStatus.fileBuffer &&
        fetchCostReportDownloadStatus.fileName
      ) {
        exportToFile(
          fetchCostReportDownloadStatus.fileBuffer,
          fetchCostReportDownloadStatus.fileName
        );
      }
      setIsDownloading(false);
    }
  }, [isDownloading, isDownloadLoading, fetchCostReportDownloadStatus]);

  const downloadCsvHandler = () => {
    setIsDownloading(true);
    fetchCostReport();
  };

  // Handle chart initialization
  const handleChartInit = (chart: echarts.ECharts | null) => {
    setChartInstance(chart);
  };

  const generateColors = (count: number) => echartsColorPalette.slice(0, count);

  const legend = data && Object.keys(data);
  const dates =
    data && Object.keys(data[legend[0]]).filter((key) => key !== 'totalCost');
  const colors = generateColors(legend?.length);

  const getChartOptions = (): echarts.EChartsOption => {
    const series = legend.map((service: any, index: number) => ({
      name: service,
      type: 'bar',
      stack: 'total',
      data: dates.map((date: any) => data[service][date]?.cost || 0),
      itemStyle: {
        color: colors[index],
      },
    }));

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
        confine: false,
        enterable: true,

        formatter: (params: any) => {
          const header = `<div class="echarts-tooltip-title">${params[0].axisValue}</div>`;

          const items = params
            .map((param: any) => {
              const colorSpan = `<span class="echarts-tooltip-color" style="background-color: ${param.color}"></span>`;
              return `${colorSpan} <span class="echarts-tooltip-name">${
                param.seriesName
              } :</span> <span class="echarts-tooltip-value">${costFormatter2(
                currencies.USD.shortHand,
                param.value
              )}</span>`;
            })
            .join('<br/>');

          return `<div class="echarts-tooltip">${header}${items}</div>`;
        },
      },
      legend: {
        show: false,
      },
      grid: {
        left: '1%',
        right: '2%',
        bottom: '4%',
        top: '5%',
        containLabel: true,
      },
      dataZoom: [
        {
          type: 'inside',
          show: true,
        },
      ],
      xAxis: {
        type: 'category',
        data: dates,
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: '${value}',
        },
      },
      series: series,
    };
  };

  const renderChart = () => {
    if (isSubmitting) {
      return (
        <div style={{ width: '100%', height: '100%' }}>
          <GraphLoader
            style={{
              // position: 'relative',
              height: '41vh',
              width: '91%',
              margin: '5rem auto',
            }}
            isLoading={isSubmitting}
          />
        </div>
      );
    }

    if (Object.keys(servicesData).length > 0) {
      return (
        <>
          <div
            style={{
              width: '100%',
              height: '54vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ flex: 1, minHeight: 0 }}>
              <Chart
                option={getChartOptions()}
                style={{ height: '100%', width: '100%' }}
                onChartInit={handleChartInit}
              />
            </div>
            <CustomLegend
              legendData={legend}
              colors={colors}
              chartInstance={chartInstance}
            />
          </div>
        </>
      );
    }

    return <GraphLoader isLoading={false} />;
  };

  // account selection section
  const onAccountChange = (data: any) => {
    if (data.cloudType === CLOUD_PROVIDER_SERVICES.AWS)
      setSelectedAccountId(data.accountId);
    // else if (data.cloudType === CLOUD_PROVIDER_SERVICES.AZURE) {
    //   if (data.subscriptionName) {
    //     setSelectedSubscriptionId(data.subscriptionId);
    //   } else if (data.accountId) {
    //     setSelectedAccountId(data.accountId);
    //     setSelectedSubscriptionId(ALL_VALUE);
    //   }
    // }
    setTimeout(() => {
      fetch();
    }, 0);
  };

  // Filter drawer handlers

  const handleOpenFilters = () => {
    setFilterDrawerOpen(true);
  };

  const handleCloseFilters = () => {
    setFilterDrawerOpen(false);
  };

  const handleSelectFilterCategory = (
    categoryId: string,
    categoryLabel: string
  ) => {
    if (!categoryId && !categoryLabel) {
      setSelectedFilterCategory(null);
      setSelectedFilterCategoryInfo(null);
      return;
    }

    const category = { id: categoryId, label: categoryLabel };
    const categoryKey = createCategoryKey(category);

    setSelectedFilterCategory(categoryKey);
    setSelectedFilterCategoryInfo(category);
  };
  const handleSelectGroupByFilterCategory = (
    categoryId: string,
    categoryLabel: string
  ) => {
    setSelectedCategoryLabel(categoryLabel);
    const category = { id: categoryId, label: categoryLabel };
    const categoryKey = createCategoryKey(category);

    setGroupBySelectedKey(categoryKey);
    setGroupBySelectedCategory(categoryId);
    setSelectedFilterOptions({});
    setUiSelectedOptionsByCategory({});

    setTimeout(() => {
      fetch();
    }, 0);
  };

  const handleSelectFilterOptions = (categoryId: string, options: string[]) => {
    // Update selectedFilterOptions directly with the merged distinct options
    if (options.length === 0) {
      // Use functional update to ensure we get the latest state
      setSelectedFilterOptions((prevOptions) => {
        const newOptions = { ...prevOptions };
        delete newOptions[categoryId];
        return newOptions;
      });
    } else {
      // Use functional update to ensure we get the latest state
      setSelectedFilterOptions((prevOptions) => ({
        ...prevOptions,
        [categoryId]: options, // options already contain merged distinct values
      }));
    }
  };

  const handleUpdateUiSelections = (categoryKey: string, options: string[]) => {
    if (options.length === 0) {
      const newUiSelections = { ...uiSelectedOptionsByCategory };
      delete newUiSelections[categoryKey];
      setUiSelectedOptionsByCategory(newUiSelections);
    } else {
      setUiSelectedOptionsByCategory({
        ...uiSelectedOptionsByCategory,
        [categoryKey]: options,
      });
    }
  };

  const handleApplyFilters = () => {
    setFilterDrawerOpen(false);
    fetch();
  };
  const handleResetFilters = () => {
    if (selectedFilterCategoryInfo) {
      const categoryKey = createCategoryKey(selectedFilterCategoryInfo);

      const newSelectedOptions = { ...selectedFilterOptions };
      delete newSelectedOptions[selectedFilterCategoryInfo.id];
      setSelectedFilterOptions(newSelectedOptions);

      // Clear from UI selections
      const newUiSelections = { ...uiSelectedOptionsByCategory };
      delete newUiSelections[categoryKey];
      setUiSelectedOptionsByCategory(newUiSelections);
    } else {
      // Reset all categories
      setSelectedFilterOptions({});
      setUiSelectedOptionsByCategory({});
    }
    setTimeout(() => {
      fetch();
    }, 0);
  };

  // Handle date range change
  const handleDateRangeChange = (start: Date, end: Date) => {
    setStartDate(start);
    setEndDate(end);
    setTimeout(() => {
      fetch();
    }, 0);
  };

  const handleTimeFrameChange = (timeFrame: TimeFrameAPI) => {
    setSelectedGroupBy(timeFrame);

    setTimeout(() => {
      fetch();
    }, 0);
  };

  // const quarterPresets = [
  //   {
  //     label: 'Q1 2024',
  //     value: 'q1-2024',
  //     getDateRange: () => ({
  //       start: new Date(2024, 0, 1), // Jan 1
  //       end: new Date(2024, 2, 31), // Mar 31
  //     }),
  //   },
  //   {
  //     label: 'Q2 2024',
  //     value: 'q2-2024',
  //     getDateRange: () => ({
  //       start: new Date(2024, 3, 1), // Apr 1
  //       end: new Date(2024, 5, 30), // Jun 30
  //     }),
  //   },
  // ];

  return (
    <div className="app-container">
      <TitleHeader
        extraContent={<SelectedAccount onAccountChange={onAccountChange} />}
      >
        Cost Analysis
      </TitleHeader>
      <CostAnalysisHeader
        title="Group By"
        onOpenFilters={handleOpenFilters}
        filterCategories={PARENT_FILTERS}
        selectedCategory={groupBySelectedKey}
        onCategoryChange={handleSelectGroupByFilterCategory}
      />

      <div className="costAnalysisTable">
        <ServiceCostTable
          activeView={activeView}
          data={servicesData}
          onViewChange={setActiveView}
          isLoading={isSubmitting}
          selectedCategoryLabel={selectedCategoryLabel}
          onDownloadCsv={downloadCsvHandler}
          onTimeFrameChange={handleTimeFrameChange}
          isExcelDownloading={isDownloadLoading}
          // onRegionChange={(region) => setSelectedRegion(region || ALL_VALUE)}
          // onServiceChange={(service) =>
          //   setSelectedService(service || ALL_VALUE)
          // }
          renderChart={renderChart}
          dateRangePicker={
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onDateChange={handleDateRangeChange}
              showPresetRanges={true}
              defaultStartDate={defaultStartDate}
              defaultEndDate={defaultEndDate}
            />
          }
        />
      </div>

      <FilterDrawer
        open={filterDrawerOpen}
        onClose={handleCloseFilters}
        categories={PARENT_FILTERS}
        selectedCategory={selectedFilterCategory}
        selectedOptions={selectedFilterOptions}
        onSelectCategory={handleSelectFilterCategory}
        onSelectOptions={handleSelectFilterOptions}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
        uiSelectedOptionsByCategory={uiSelectedOptionsByCategory}
        onUpdateUiSelections={handleUpdateUiSelections}
      />
    </div>
  );
};

export default CostAnalysis;
